import { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';

const PAYPAL_EMAIL = 'massimo_marchiori@yahoo.it';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/barokarc_IMG_6596.jpeg';

const DonationPopup = ({ onClose }) => {
  const { language } = useLanguage();
  const [selectedAmount, setSelectedAmount] = useState(5);
  const [showThankYou, setShowThankYou] = useState(false);

  const amounts = [2, 5, 10];

  // Genera link PayPal per la donazione
  const getPayPalLink = (amount) => {
    const params = new URLSearchParams({
      business: PAYPAL_EMAIL,
      amount: amount.toString(),
      currency_code: 'EUR',
      item_name: 'Donazione Respiro Blu',
      no_shipping: '1',
      no_note: '1'
    });
    return `https://www.paypal.com/donate?${params.toString()}`;
  };

  const handleDonate = () => {
    // Apri PayPal in una nuova finestra
    window.open(getPayPalLink(selectedAmount), '_blank');
    
    // Mostra messaggio di ringraziamento
    setShowThankYou(true);
    
    // Salva che l'utente ha donato - NON mostrerà più il popup
    localStorage.setItem('respiro_donated', 'true');
    
    // Chiudi dopo 3 secondi
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  const handleLater = () => {
    // NON salva nulla - il popup riapparirà alla prossima apertura
    onClose();
  };

  if (showThankYou) {
    return (
      <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/50 rounded-2xl p-8 max-w-sm w-full text-center animate-pulse">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
            <img src={LOGO_URL} alt="Respiro Blu" className="w-full h-full object-cover" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2"><TranslatedText text="Grazie!" /></h3>
          <p className="text-cyan-400">
            <TranslatedText text="Il tuo supporto fa la differenza per il nostro mare" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div key={`donation-${language}`} className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={handleLater}>
      <div 
        className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/50 rounded-2xl p-6 max-w-sm w-full relative overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Decorazione sfondo */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl" />
        
        {/* Pulsante chiudi */}
        <button
          onClick={handleLater}
          className="absolute top-4 right-4 p-1 text-slate-500 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Contenuto */}
        <div className="relative">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full overflow-hidden shadow-lg shadow-cyan-500/30">
              <img src={LOGO_URL} alt="Respiro Blu" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Titolo */}
          <h3 className="text-xl font-bold text-white text-center mb-2">
            <TranslatedText text="Sostieni Respiro Blu" />
          </h3>
          <p className="text-slate-400 text-center text-sm mb-4">
            <TranslatedText text="Aiutaci a proteggere il nostro mare. Ogni donazione conta!" />
          </p>
          
          {/* Info rivista */}
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mb-4">
            <p className="text-cyan-400 text-xs text-center">
              📖 <TranslatedText text="Con una donazione di 5€ ricevi la Rivista Respiro Blu in formato digitale senza filigrana" />
            </p>
          </div>

          {/* Selezione importo */}
          <div className="flex justify-center gap-3 mb-6">
            {amounts.map((amount) => (
              <button
                key={amount}
                onClick={() => setSelectedAmount(amount)}
                className={`
                  w-20 h-16 rounded-xl font-bold text-lg transition-all
                  ${selectedAmount === amount
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30 scale-105'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  }
                `}
              >
                {amount}€
              </button>
            ))}
          </div>

          {/* Pulsante dona */}
          <button
            onClick={handleDonate}
            className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/30 transition-all hover:scale-[1.02]"
          >
            <span><TranslatedText text={`Dona ${selectedAmount}€ con PayPal`} /></span>
            <ExternalLink className="w-4 h-4" />
          </button>

          {/* Link più tardi */}
          <button
            onClick={handleLater}
            className="w-full mt-3 py-2 text-slate-500 hover:text-slate-300 text-sm transition-colors"
          >
            <TranslatedText text="Magari più tardi" />
          </button>

          {/* Footer */}
          <p className="text-center text-xs text-slate-600 mt-4">
            <TranslatedText text="100% delle donazioni va al progetto" />
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationPopup;
